export const DOCUMENT_FORMAT_CATALOG = [
  {
    extension: ".pdf",
    mimeTypes: ["application/pdf"],
    displayKey: "pdf",
  },
  {
    extension: ".doc",
    mimeTypes: ["application/msword"],
    displayKey: "doc",
  },
  {
    extension: ".docx",
    mimeTypes: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
    displayKey: "docx",
  },
  {
    extension: ".odt",
    mimeTypes: ["application/vnd.oasis.opendocument.text"],
    displayKey: "odt",
  },
  {
    extension: ".xls",
    mimeTypes: ["application/vnd.ms-excel"],
    displayKey: "xls",
  },
  {
    extension: ".xlsx",
    mimeTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    displayKey: "xlsx",
  },
  {
    extension: ".csv",
    mimeTypes: ["text/csv", "application/csv"],
    displayKey: "csv",
  },
  {
    extension: ".jpg",
    mimeTypes: ["image/jpeg", "image/jpg"],
    displayKey: "jpg",
  },
  {
    extension: ".jpeg",
    mimeTypes: ["image/jpeg", "image/jpg"],
    displayKey: "jpeg",
  },
  {
    extension: ".png",
    mimeTypes: ["image/png"],
    displayKey: "png",
  },
  {
    extension: ".gif",
    mimeTypes: ["image/gif"],
    displayKey: "gif",
  },
  {
    extension: ".webp",
    mimeTypes: ["image/webp"],
    displayKey: "webp",
  },
] as const;

export type DocumentFormat = (typeof DOCUMENT_FORMAT_CATALOG)[number];
export type DocumentFileExtension = DocumentFormat["extension"];
export type DocumentMimeType = DocumentFormat["mimeTypes"][number];
export type DocumentDisplayKey = DocumentFormat["displayKey"];

function createDocumentFileExtensions(
  catalog: typeof DOCUMENT_FORMAT_CATALOG
): readonly DocumentFileExtension[] {
  return Object.freeze(catalog.map(({ extension }) => extension));
}

function createDocumentMimeTypes(
  catalog: typeof DOCUMENT_FORMAT_CATALOG
): readonly DocumentMimeType[] {
  const mimeTypes: DocumentMimeType[] = [];
  const uniqueMimeTypes = new Set<DocumentMimeType>();

  for (const format of catalog) {
    for (const mimeType of format.mimeTypes) {
      if (uniqueMimeTypes.has(mimeType)) {
        continue;
      }

      uniqueMimeTypes.add(mimeType);
      mimeTypes.push(mimeType);
    }
  }

  return Object.freeze(mimeTypes);
}

function createDocumentAcceptValue(extensions: readonly DocumentFileExtension[]): string {
  return extensions.join(",");
}

function createSupportedFormatsLabel(extensions: readonly DocumentFileExtension[]): string {
  return extensions.map((extension) => extension.slice(1)).join(", ");
}

export const DOCUMENT_FILE_EXTENSIONS = createDocumentFileExtensions(DOCUMENT_FORMAT_CATALOG);
export const DOCUMENT_MIME_TYPES = createDocumentMimeTypes(DOCUMENT_FORMAT_CATALOG);
export const DOCUMENT_ACCEPT_VALUE = createDocumentAcceptValue(DOCUMENT_FILE_EXTENSIONS);
export const DOCUMENT_SUPPORTED_FORMATS_LABEL =
  createSupportedFormatsLabel(DOCUMENT_FILE_EXTENSIONS);

export function extractDocumentFileExtension(fileName: string): string {
  const normalizedFileName = fileName.trim().toLowerCase();
  const finalDotIndex = normalizedFileName.lastIndexOf(".");

  if (finalDotIndex < 1 || finalDotIndex === normalizedFileName.length - 1) {
    return "";
  }

  return normalizedFileName.slice(finalDotIndex);
}

export function getDocumentFormatByExtension(extension: string): DocumentFormat | undefined {
  const normalizedExtension = extension.trim().toLowerCase();
  let dottedExtension = normalizedExtension;

  if (!normalizedExtension.startsWith(".")) {
    dottedExtension = `.${normalizedExtension}`;
  }

  return DOCUMENT_FORMAT_CATALOG.find((format) => format.extension === dottedExtension);
}

export function getDocumentFormatByMimeType(mimeType: string): DocumentFormat | undefined {
  const normalizedMimeType = mimeType.trim().toLowerCase();

  return DOCUMENT_FORMAT_CATALOG.find((format) =>
    format.mimeTypes.some((candidateMimeType) => candidateMimeType === normalizedMimeType)
  );
}

export function getDocumentFormat(format: string): DocumentFormat | undefined {
  const extensionFormat = getDocumentFormatByExtension(format);

  if (extensionFormat) {
    return extensionFormat;
  }

  return getDocumentFormatByMimeType(format);
}

export function isSupportedDocumentFileName(fileName: string): boolean {
  const extension = extractDocumentFileExtension(fileName);

  if (!extension) {
    return false;
  }

  return Boolean(getDocumentFormatByExtension(extension));
}

function getDocumentFileName(file: File | Blob): string {
  if (!("name" in file) || typeof file.name !== "string") {
    return "";
  }

  return file.name;
}

export function isSupportedDocumentFile(file: File | Blob): boolean {
  return isSupportedDocumentFileName(getDocumentFileName(file));
}
